import type { Vote } from '@filmsloth/shared';

/** How far a drag must travel before it counts as a vote rather than a wobble. */
export const SWIPE_THRESHOLD_PX = 90;

/**
 * A drag can also be *fast* rather than long: a short flick is how people
 * actually swipe, and requiring the full distance makes the deck feel stuck.
 */
export const SWIPE_VELOCITY_PX_PER_MS = 0.6;

export interface Drag {
  dx: number;
  dy: number;
  /** Milliseconds since the drag started; used for the flick shortcut. */
  elapsed: number;
}

/**
 * Which vote a finished drag stands for, or `null` to snap the card back.
 *
 * Left is No and right is Yes, matching the buttons underneath and the
 * arrow keys. Down is "don't mind" — the neutral middle, and the one
 * direction that cannot be confused with the other two.
 *
 * Up is deliberately unassigned. It is where "undo" would go by analogy with
 * other card apps, but an accidental flick upward that silently rewrote your
 * last answer would be worse than no gesture at all.
 */
export function voteForDrag(drag: Drag): Vote | null {
  const { dx, dy, elapsed } = drag;
  const horizontal = Math.abs(dx);
  const vertical = Math.abs(dy);

  // A flick counts even when short, as long as it was decisive.
  const speed = elapsed > 0 ? Math.max(horizontal, vertical) / elapsed : 0;
  const flicked = speed >= SWIPE_VELOCITY_PX_PER_MS;

  if (horizontal >= vertical) {
    if (horizontal < SWIPE_THRESHOLD_PX && !flicked) return null;
    return dx > 0 ? 'like' : 'dislike';
  }

  // Only downward: an upward drag is usually the page trying to scroll.
  if (dy <= 0) return null;
  if (vertical < SWIPE_THRESHOLD_PX && !flicked) return null;
  return 'neutral';
}

/**
 * How far along the gesture is, 0–1, for fading in the choice indicator.
 * Clamped so the badge does not keep getting louder past the point of decision.
 */
export function dragProgress(drag: Pick<Drag, 'dx' | 'dy'>): number {
  const distance = Math.max(Math.abs(drag.dx), Math.abs(drag.dy));
  return Math.min(1, distance / SWIPE_THRESHOLD_PX);
}

/** The vote a drag *would* cast if released now — for live feedback. */
export function previewVote(drag: Pick<Drag, 'dx' | 'dy'>): Vote | null {
  const horizontal = Math.abs(drag.dx);
  const vertical = Math.abs(drag.dy);
  if (horizontal < 20 && vertical < 20) return null;
  if (horizontal >= vertical) return drag.dx > 0 ? 'like' : 'dislike';
  return drag.dy > 0 ? 'neutral' : null;
}

/**
 * How far a touch may wander before the card and the page settle who owns it.
 *
 * Deliberately under the browser's own scroll threshold (about 8px on
 * Android): the call has to be made on a move the browser is still willing to
 * have cancelled, or the page is already scrolling by the time we have an
 * opinion.
 */
export const AXIS_LOCK_SLOP_PX = 6;

/** Who a touch on the card belongs to, once it has moved far enough to tell. */
export type GestureOwner = 'card' | 'page' | 'undecided';

/**
 * Whether a touch on the card is a swipe or the beginning of a page scroll.
 *
 * `touch-action: pan-y` on its own hands *every* vertical drag to the browser,
 * which is why the downward swipe — the neutral vote — used to scroll the
 * page instead of answering: the browser took the gesture over and the card
 * never heard the end of it. So the page keeps the vertical axis only where it
 * has somewhere to go:
 *
 * - sideways is always the card's; the page has no horizontal scroll to lose
 * - upward is always the page's; up is not a vote, and dragging up is how you
 *   read the rest of a card
 * - downward is the card's while the page is at the top, and the page's
 *   otherwise — the bargain a bottom sheet strikes: the content comes back to
 *   the top first, and from there the gesture is the gesture
 */
export function gestureOwner(
  drag: Pick<Drag, 'dx' | 'dy'>,
  pageCanScrollUp: boolean,
): GestureOwner {
  const horizontal = Math.abs(drag.dx);
  const vertical = Math.abs(drag.dy);

  if (horizontal < AXIS_LOCK_SLOP_PX && vertical < AXIS_LOCK_SLOP_PX) return 'undecided';
  if (horizontal >= vertical) return 'card';
  if (drag.dy < 0) return 'page';
  return pageCanScrollUp ? 'page' : 'card';
}
